import { menuStructure } from '../config/menuStructure';
import { filterNavigationMenu } from './filterNavigationMenu';

const normalizePath = (path) => {
  if (!path) return '/';
  let normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
};

/** Routes always reachable when authenticated (not shown in sidebar). */
const ALWAYS_ALLOWED_PATHS = new Set(['/', '/profile']);

/** Detail/child routes allowed when the parent list page is in the filtered sidebar. */
const PATH_PREFIX_PARENTS = [
  ['/users/', '/users'],
  ['/project-workspace/', '/project-permitting'],
  ['/compliance-orders/', '/compliance-orders'],
  ['/audit-scopes/', '/audit-scopes'],
  ['/question-banks/', '/audit-scopes'],
  ['/audit-detail/', '/audits'],
  ['/add-audit-record/', '/audits'],
  ['/conoco-task-groups/', '/conoco-task-groups'],
  ['/custom-form-builder/studio/', '/custom-form-builder'],
  ['/custom-report-builder/studio/', '/custom-report-builder'],
  ['/custom-report-builder/view/', '/custom-report-builder']
];

const EXACT_CHILD_ROUTES = {
  '/add-audit-master': '/audits',
  '/engines/add': '/engines'
};

/** Popout modal keys map to the parent navigation path for access checks. */
const POPOUT_MODAL_NAV_PATHS = {
  'compliance-order-verification': '/compliance-order-verifications',
  'edit-project': '/project-permitting',
  'add-edit-permit': '/project-permitting',
  'add-edit-permit-task': '/project-permitting',
  'add-edit-action-log': '/project-permitting',
  'add-edit-status-report': '/project-permitting',
  'customer-form': '/compliance-orders',
  'attachment-viewer': '/library-of-records',
  'drainage-recordation-form': '/drainage-recordation',
  'compliance-record-form': '/compliance',
  'compliance-verification-record-form': '/audits',
  'compliance-verification-master-form': '/audits',
  'compliance-verification-form': '/audits',
  'spcc-routine-form': '/spcc-routine-inspection',
  'spcc-annual-form': '/spcc-annual-inspection',
  'swppp-discharge-form': '/swppp-discharge-monitoring-inspection',
  'swppp-quarterly-form': '/swppp-quarterly-inspection',
  'environmental-sampling-form': '/environmental-sampling',
  'environmental-lab-analysis-form': '/environmental-lab-analysis',
  'sump-pump-maintenance-form': '/sump-pump-maintenance-inspection',
  'response-equipment-inventory-form': '/response-equipment-inventory-inspection',
  'transfer-hose-and-pipe-form': '/transfer-hose-and-pipe-testing',
  'authorized-waste-receipt-form': '/authorized-waste-receipt',
  'analytical-result-form-for-awr-form': '/analytical-result-form-for-awr',
  'compliance-order-form': '/compliance-orders',
  'field-form': '/fields',
  'well-form': '/wells',
  'hazardous-waste-removal-form': '/hazardous-waste-removal',
  'air-glycol-pump-form': '/air-glycol-pump-log',
  'air-opacity-monitoring-form': '/air-opacity-monitoring',
  'annual-tabletop-exercise-log-form': '/annual-tabletop-exercise-log',
  'equipment-deployment-exercise-log-form': '/equipment-deployment-exercise-log',
  'qi-notification-exercise-log-form': '/qi-notification-exercise-log',
  PWSWeeklyWaterSample: '/weekly-pws-water-monitoring',
  PWSMonthlyWaterSample: '/monthly-pws-water-sample',
  MonthlyPWSAnalytical: '/monthly-pws-analytical-report-review',
  QuarterlyDLQOR: '/quarterly-dlqor',
  FlowMeterLogs: '/weekly-flow-meter-logs',
  ColorimeterLogs: '/quarterly-colorimeter-calibration-verification',
  PWSEquipmentServiceLogs: '/weekly-equipment-service-logs',
  ReclaimedWaterWeekly: '/weekly-reclaimed-water-monitoring',
  ReclaimedWaterMonthly: '/monthly-reclaimed-water-sample',
  MonthlyReclaimedAnalytical: '/monthly-reclaimed-analytical-report-review',
  ReclaimedWaterBiannual: '/semi-annual-reclaimed-water-sample',
  BiannualReclaimedAnalytical: '/semi-annual-reclaimed-analytical-report-review',
  ReclaimedWaterAnnual: '/annual-reclaimed-water-sample',
  AnnualReclaimedAnalytical: '/annual-reclaimed-analytical-report-review'
};

export function flattenNavigationPaths(menuItems = []) {
  const paths = new Set();
  const walk = (items) => {
    for (const item of items || []) {
      if (item.type === 'section' && item.children?.length) {
        walk(item.children);
      } else if (item.path) {
        paths.add(normalizePath(item.path));
      }
    }
  };
  walk(menuItems);
  return paths;
}

export function getFilteredNavigationMenuForUser(user) {
  if (!user) return [];
  return filterNavigationMenu(menuStructure, user);
}

export function getAccessibleNavigationPathsForUser(user) {
  return flattenNavigationPaths(getFilteredNavigationMenuForUser(user));
}

export function isNavigationPathAllowed(pathname, menuItems) {
  const path = normalizePath(pathname);

  if (ALWAYS_ALLOWED_PATHS.has(path)) {
    return true;
  }

  const allowedPaths = flattenNavigationPaths(menuItems);

  if (allowedPaths.has(path)) {
    return true;
  }

  const exactParent = EXACT_CHILD_ROUTES[path];
  if (exactParent && allowedPaths.has(exactParent)) {
    return true;
  }

  for (const [prefix, parentPath] of PATH_PREFIX_PARENTS) {
    if (path.startsWith(prefix) && allowedPaths.has(parentPath)) {
      return true;
    }
  }

  // Hidden while the report builder is in testing. Not in the sidebar.
  if (path === '/custom-report-builder' || path.startsWith('/custom-report-builder/')) {
    return true;
  }

  // Reporters open their own list and a single report from the header menu.
  // The sidebar item stays limited to the reviewer.
  if (path === '/bug-reports' || path.startsWith('/bug-reports/')) {
    return true;
  }

  if (path.startsWith('/popout/')) {
    const segments = path.split('/').filter(Boolean);
    const modalKey = segments[1];
    const parentPath = POPOUT_MODAL_NAV_PATHS[modalKey];
    if (!parentPath) return false;
    return allowedPaths.has(parentPath);
  }

  return false;
}

export function canUserAccessNavigationPath(user, pathname) {
  if (!user) return false;
  const filteredMenu = getFilteredNavigationMenuForUser(user);
  return isNavigationPathAllowed(pathname, filteredMenu);
}
